import { animate, AnimationBuilder, AnimationPlayer, style } from '@angular/animations';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HeaderConfig } from '@core/models/header-config';
import { LayoutService } from '@core/services/layout.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, filter, interval, take } from 'rxjs';
import { LANGUAGE_KEY } from 'src/app/app.module';

@Component({
    selector: 'app-layout-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  private readonly animationBuilder = inject(AnimationBuilder);
  private readonly languageService = inject(TranslateService);
  private readonly layoutService = inject(LayoutService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);

  @ViewChild("mainContent") mainContent: ElementRef;

  player: AnimationPlayer;

  ngOnInit(): void {
    if (!this.cookieService.get(LANGUAGE_KEY)) {
      this.setLanguageSelectionStyle('flex');
      this._setupLanguage();
    } else {
      this._handleSplashScreen();
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.mainContent.nativeElement.behavior = 'smooth';
      this.mainContent.nativeElement.scrollTop = 0;
    });
  }

  get headerConfig(): HeaderConfig {
    return this.layoutService.headerConfig$();
  }

  get translateX(): number {
    return ((document.body.clientWidth > 576 ? 576 : document.body.clientWidth) / 2) - (52 / 2);
  }

  get translateY(): number {
    return (document.body.clientHeight / 2) - (45 / 2) - 50;
  }

  get scale(): number {
    return Math.floor((document.body.clientHeight * 3.55) / 100);
  }

  get animationStyles() {
    return {
      '--translateX': `${this.translateX}px`,
      '--translateY': `${this.translateY}px`,
      '--max-scale': this.scale
    };
  }

  private _setupLanguage(): void {
    const languages: NodeListOf<HTMLElement> | null = document.querySelectorAll('.language');
    if (languages) {
      languages.forEach((language: HTMLElement) => {
        language.addEventListener('click', () => {
          this.languageService.setDefaultLang((language as HTMLElement).dataset['lang']);
          this.languageService.use((language as HTMLElement).dataset['lang']);
          this.cookieService.set(LANGUAGE_KEY, (language as HTMLElement).dataset['lang'], { expires: this.cookieExpireDate });
          document.querySelector("#s-path").classList.add("animate");
          document.querySelector("#splashContent").classList.add("animate");
          this.setLanguageSelectionStyle('none');
          setTimeout(() => {
            this._handleSplashScreen();
          }, 1750);
        });
      });
    }
  }

  private setLanguageSelectionStyle(style: string): void {
    const languageSelection: HTMLElement | null = document.querySelector(".language-selection");
    if (languageSelection) {
      languageSelection.style.display = style;
    }
  }

  private _handleSplashScreen(): void {
      const splashScreen: HTMLElement | null = document.querySelector('.Splash');
      if (splashScreen) {
        if (!environment.splashScreen) {
          splashScreen.remove();
          return;
        }

        const splashScreenDuration = 200;
        const animationDuration = 200;

        this.player = this.animationBuilder
          .build(animate(animationDuration, style({ opacity: 0 })))
          .create(splashScreen);

        combineLatest([
          interval(splashScreenDuration).pipe(take(1)),
        ]).subscribe(() => {
          this.player.play();

          setTimeout(() => {
            splashScreen.remove();
          }, animationDuration);
        });
      }
  }

  get cookieExpireDate(): Date {
    const _date = new Date();
    _date.setFullYear(_date.getFullYear() + 1);

    return _date;
  }
}
